import React, { useState, useEffect } from "react";
import {
  Layers,
  Cpu,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Search,
  CheckCircle2,
  AlertCircle,
  Tag,
} from "lucide-react";
import { projectsService } from "@/api/projectsService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

const slugify = (text) =>
  (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/**
 * ============================================================
 * ADMIN TAXONOMY MANAGER (/admin/taxonomy)
 * ============================================================
 * Unified management interface for normalized Categories and
 * Technologies tables supporting portfolio case studies.
 */
export default function AdminTaxonomy() {
  const [activeTab, setActiveTab] = useState("categories"); // 'categories' | 'technologies'
  const [categories, setCategories] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search filters
  const [catSearch, setCatSearch] = useState("");
  const [techSearch, setTechSearch] = useState("");

  // New Category Form
  const [newCatName, setNewCatName] = useState("");
  const [newCatSlug, setNewCatSlug] = useState("");
  const [newCatDesc, setNewCatDesc] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);

  // Edit Category Form
  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const [editCatSlug, setEditCatSlug] = useState("");
  const [editCatDesc, setEditCatDesc] = useState("");

  // New Tech Form
  const [newTechName, setNewTechName] = useState("");
  const [newTechSlug, setNewTechSlug] = useState("");
  const [creatingTech, setCreatingTech] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [cats, techs] = await Promise.all([
        projectsService.getCategories(),
        projectsService.getTechnologies(),
      ]);
      setCategories(cats);
      setTechnologies(techs);
    } catch (err) {
      console.error("[AdminTaxonomy] Load error:", err);
      toast.error("Failed to load taxonomy catalogs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handlers: Category
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;

    try {
      setCreatingCat(true);
      const created = await projectsService.createCategory({
        name: newCatName.trim(),
        slug: newCatSlug.trim() || slugify(newCatName),
        description: newCatDesc.trim(),
      });
      toast.success(`Category "${created.name}" created.`);
      setNewCatName("");
      setNewCatSlug("");
      setNewCatDesc("");
      loadData();
    } catch (err) {
      toast.error(err.message || "Failed to create category.");
    } finally {
      setCreatingCat(false);
    }
  };

  const handleStartEditCat = (cat) => {
    setEditingCatId(cat.id);
    setEditCatName(cat.name);
    setEditCatSlug(cat.slug);
    setEditCatDesc(cat.description || "");
  };

  const handleSaveEditCat = async (id) => {
    try {
      await projectsService.updateCategory(id, {
        name: editCatName.trim(),
        slug: editCatSlug.trim() || slugify(editCatName),
        description: editCatDesc.trim(),
      });
      toast.success("Category updated.");
      setEditingCatId(null);
      loadData();
    } catch (err) {
      toast.error(err.message || "Failed to update category.");
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? This will fail if projects are still assigned to it.`)) return;
    try {
      await projectsService.deleteCategory(cat.id);
      toast.success(`Category "${cat.name}" deleted.`);
      loadData();
    } catch (err) {
      toast.error(err.message || "Failed to delete category.");
    }
  };

  // Handlers: Technology
  const handleCreateTechnology = async (e) => {
    e.preventDefault();
    if (!newTechName.trim()) return;

    try {
      setCreatingTech(true);
      const created = await projectsService.createTechnology({
        name: newTechName.trim(),
        slug: newTechSlug.trim() || slugify(newTechName),
      });
      toast.success(`Technology "${created.name}" added.`);
      setNewTechName("");
      setNewTechSlug("");
      loadData();
    } catch (err) {
      toast.error(err.message || "Failed to add technology.");
    } finally {
      setCreatingTech(false);
    }
  };

  const handleDeleteTechnology = async (tech) => {
    if (!window.confirm(`Delete technology "${tech.name}"?`)) return;
    try {
      await projectsService.deleteTechnology(tech.id);
      toast.success(`Technology "${tech.name}" deleted.`);
      loadData();
    } catch (err) {
      toast.error(err.message || "Failed to delete technology.");
    }
  };

  const filteredCategories = categories.filter(
    (c) =>
      c.name.toLowerCase().includes(catSearch.toLowerCase()) ||
      c.slug.toLowerCase().includes(catSearch.toLowerCase())
  );

  const filteredTechnologies = technologies.filter(
    (t) =>
      t.name.toLowerCase().includes(techSearch.toLowerCase()) ||
      t.slug.toLowerCase().includes(techSearch.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-16">
      {/* Top Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
          <Layers className="w-6 h-6 text-cyan-600" />
          <span>Taxonomy & Metadata Catalogs</span>
        </h1>
        <p className="text-xs text-slate-600 font-medium mt-1">
          Manage normalized project categories and technical skills used for filtering and stack attribution.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b border-slate-200 text-xs gap-1">
        <button
          type="button"
          onClick={() => setActiveTab("categories")}
          className={cn(
            "px-5 py-2.5 font-semibold transition-colors border-b-2 flex items-center gap-2",
            activeTab === "categories"
              ? "border-cyan-600 text-cyan-600"
              : "border-transparent text-slate-500 hover:text-slate-900"
          )}
        >
          <Layers className="w-3.5 h-3.5 text-cyan-600" />
          <span>Project Categories ({categories.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("technologies")}
          className={cn(
            "px-5 py-2.5 font-semibold transition-colors border-b-2 flex items-center gap-2",
            activeTab === "technologies"
              ? "border-cyan-600 text-cyan-600"
              : "border-transparent text-slate-500 hover:text-slate-900"
          )}
        >
          <Cpu className="w-3.5 h-3.5 text-cyan-600" />
          <span>Technologies & Frameworks ({technologies.length})</span>
        </button>
      </div>

      {/* TAB 1: CATEGORIES */}
      {activeTab === "categories" && (
        <div className="space-y-6">
          {/* Create Category Form */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 shadow-xs">
            <h2 className="text-xs font-bold text-cyan-700 uppercase tracking-wider">
              Add New Category
            </h2>
            <form onSubmit={handleCreateCategory} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs text-slate-700 font-semibold block mb-1">Category Name *</Label>
                <Input
                  type="text"
                  placeholder="e.g. AI Platform"
                  value={newCatName}
                  onChange={(e) => {
                    setNewCatName(e.target.value);
                    setNewCatSlug(slugify(e.target.value));
                  }}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white"
                  required
                />
              </div>

              <div>
                <Label className="text-xs text-slate-700 font-semibold block mb-1">URL Slug</Label>
                <Input
                  type="text"
                  placeholder="e.g. ai-platform"
                  value={newCatSlug}
                  onChange={(e) => setNewCatSlug(slugify(e.target.value))}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 font-mono rounded-xl focus:bg-white"
                  required
                />
              </div>

              <div>
                <Label className="text-xs text-slate-700 font-semibold block mb-1">Description (Optional)</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Short domain summary"
                    value={newCatDesc}
                    onChange={(e) => setNewCatDesc(e.target.value)}
                    className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white"
                  />
                  <Button
                    type="submit"
                    disabled={creatingCat || !newCatName.trim()}
                    className="h-10 px-5 rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-xs font-semibold shrink-0 shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    <span>Create</span>
                  </Button>
                </div>
              </div>
            </form>
          </div>

          {/* Categories Listing */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 shadow-xs">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-sm font-bold text-slate-900">Active Categories ({filteredCategories.length})</h3>
              <div className="relative w-64">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="text"
                  placeholder="Search categories..."
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  className="pl-8 h-8 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-lg focus:bg-white"
                />
              </div>
            </div>

            <div className="divide-y divide-slate-100">
              {filteredCategories.map((cat) => (
                <div key={cat.id} className="py-3 flex items-center justify-between gap-4">
                  {editingCatId === cat.id ? (
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input
                        value={editCatName}
                        onChange={(e) => setEditCatName(e.target.value)}
                        className="h-8 bg-white border-slate-200 text-xs text-slate-900"
                      />
                      <Input
                        value={editCatSlug}
                        onChange={(e) => setEditCatSlug(e.target.value)}
                        className="h-8 bg-white border-slate-200 text-xs text-slate-900 font-mono"
                      />
                      <Input
                        value={editCatDesc}
                        onChange={(e) => setEditCatDesc(e.target.value)}
                        className="h-8 bg-white border-slate-200 text-xs text-slate-900"
                      />
                    </div>
                  ) : (
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-900">{cat.name}</span>
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 font-mono text-[10px]">
                          {cat.slug}
                        </Badge>
                      </div>
                      {cat.description && (
                        <p className="text-[11px] text-slate-500 font-medium mt-0.5 truncate">{cat.description}</p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 shrink-0">
                    {editingCatId === cat.id ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleSaveEditCat(cat.id)}
                          className="h-8 px-3 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 text-xs font-semibold gap-1"
                        >
                          <Save className="w-3 h-3" />
                          <span>Save</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingCatId(null)}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700 rounded-lg"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleStartEditCat(cat)}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700 rounded-lg"
                          title="Edit category"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteCategory(cat)}
                          className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50 rounded-lg"
                          title="Delete category"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: TECHNOLOGIES */}
      {activeTab === "technologies" && (
        <div className="space-y-6">
          {/* Create Tech Form */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 shadow-xs">
            <h2 className="text-xs font-bold text-cyan-700 uppercase tracking-wider">
              Add New Technology
            </h2>
            <form onSubmit={handleCreateTechnology} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs text-slate-700 font-semibold block mb-1">Technology Name *</Label>
                <Input
                  type="text"
                  placeholder="e.g. Next.js 16"
                  value={newTechName}
                  onChange={(e) => {
                    setNewTechName(e.target.value);
                    setNewTechSlug(slugify(e.target.value));
                  }}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white"
                  required
                />
              </div>

              <div>
                <Label className="text-xs text-slate-700 font-semibold block mb-1">Unique Slug</Label>
                <Input
                  type="text"
                  placeholder="e.g. nextjs-16"
                  value={newTechSlug}
                  onChange={(e) => setNewTechSlug(slugify(e.target.value))}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 font-mono rounded-xl focus:bg-white"
                  required
                />
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={creatingTech || !newTechName.trim()}
                  className="h-10 px-5 rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-xs font-semibold shadow-sm w-full sm:w-auto"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  <span>Add Technology</span>
                </Button>
              </div>
            </form>
          </div>

          {/* Technologies Badges Grid */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 shadow-xs">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-sm font-bold text-slate-900">Registered Technologies ({filteredTechnologies.length})</h3>
              <div className="relative w-64">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="text"
                  placeholder="Filter technologies..."
                  value={techSearch}
                  onChange={(e) => setTechSearch(e.target.value)}
                  className="pl-8 h-8 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-lg focus:bg-white"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2.5 pt-2">
              {filteredTechnologies.map((tech) => (
                <div
                  key={tech.id}
                  className="px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:border-cyan-300 hover:bg-cyan-50/50 transition-colors flex items-center gap-2 group shadow-xs"
                >
                  <span className="text-xs text-slate-900 font-semibold">{tech.name}</span>
                  <span className="text-[10px] text-slate-400 font-mono">({tech.slug})</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteTechnology(tech)}
                    className="text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete technology"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
